import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Check, Minus, Loader2, Lock, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { groupsForUser } from '../lib/menu';

function Box({ state, disabled, onClick, testid }) {
  // state: 'on' | 'off' | 'some'
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid} aria-checked={state === 'on'} role="checkbox"
      className={`w-4 h-4 shrink-0 rounded-sm border flex items-center justify-center transition-colors ${
        state === 'off' ? 'border-border bg-secondary/40' : 'border-blue-500 bg-blue-600 text-white'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-400'}`}>
      {state === 'on' && <Check className="w-3 h-3" strokeWidth={3} />}
      {state === 'some' && <Minus className="w-3 h-3" strokeWidth={3} />}
    </button>
  );
}

// Admin: tick which sidebar entries this member sees. Only restricts within what the role allows; pages stay reachable via links.
export function MenuVisibilityDialog({ member, open, onOpenChange, onSaved }) {
  const [hidden, setHidden] = useState(new Set());
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setHidden(new Set(member?.hidden_menu_items || [])); }, [open, member]);

  const groups = useMemo(() => groupsForUser(member, { timeclockEnabled: true }), [member]);
  const all = groups.flatMap(g => g.items);
  const visibleCount = all.filter(it => !hidden.has(it.id)).length;

  const toggle = (id) => setHidden(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleGroup = (g) => setHidden(prev => {
    const n = new Set(prev);
    const ids = g.items.filter(it => !it.locked).map(it => it.id);
    const allOn = ids.every(id => !n.has(id));
    ids.forEach(id => allOn ? n.add(id) : n.delete(id));
    return n;
  });
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.setUserMenu(member.id, [...hidden]);
      toast.success(r.data.message);
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setSaving(false);
  };

  if (!member) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-card border-border max-h-[85vh] flex flex-col" data-testid="menu-visibility-dialog">
        <DialogHeader>
          <DialogTitle className="text-foreground">Menü anpassen — {member.name}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Angehakte Einträge erscheinen in der Seitenleiste von {member.name.split(' ')[0]}. Nur die Optik des Menüs — Querverweise aus anderen Seiten funktionieren weiter, Rollen-Rechte bleiben unverändert.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto custom-scrollbar -mx-1 px-1 space-y-3" data-testid="menu-visibility-groups">
          {groups.map(g => {
            const ids = g.items.map(it => it.id);
            const on = ids.filter(id => !hidden.has(id)).length;
            const state = on === 0 ? 'off' : on === ids.length ? 'on' : 'some';
            return (
              <div key={g.id} className="border border-border rounded-sm bg-secondary/20" data-testid={`menu-group-${g.id}`}>
                <div className="flex items-center gap-2.5 px-3 py-2 border-b border-border/60">
                  <Box state={state} onClick={() => toggleGroup(g)} testid={`menu-group-toggle-${g.id}`} />
                  <span className="text-xs uppercase tracking-wider font-bold text-foreground">{g.label}</span>
                  <span className="ml-auto text-[10px] font-mono text-muted-foreground" data-testid={`menu-group-count-${g.id}`}>{on}/{ids.length}</span>
                </div>
                <div className="px-3 py-1.5 grid grid-cols-2 gap-x-4">
                  {g.items.map(it => {
                    const shown = !hidden.has(it.id);
                    return (
                      <label key={it.id} className={`flex items-center gap-2.5 py-1.5 text-sm ${shown ? 'text-foreground' : 'text-muted-foreground/60 line-through'}`}>
                        <Box state={shown ? 'on' : 'off'} disabled={it.locked} onClick={() => toggle(it.id)} testid={`menu-item-toggle-${g.id}-${it.id}`} />
                        <span className="truncate">{it.label}</span>
                        {it.locked && <Lock className="w-3 h-3 text-muted-foreground/60 shrink-0" title="Immer sichtbar" />}
                        {it.hint && <span className="text-[10px] text-muted-foreground/70 truncate">· {it.hint}</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-2 pt-2 border-t border-border">
          <span className="text-[11px] font-mono text-muted-foreground" data-testid="menu-visibility-summary">{visibleCount} von {all.length} Einträgen sichtbar</span>
          <Button variant="ghost" size="sm" onClick={() => setHidden(new Set())} disabled={hidden.size === 0} data-testid="menu-visibility-reset"
            className="ml-auto h-8 text-xs text-muted-foreground hover:text-foreground">
            <RotateCcw className="w-3 h-3 mr-1" /> Alle anzeigen
          </Button>
          <Button onClick={save} disabled={saving} data-testid="menu-visibility-save" className="h-8 bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs">
            {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Speichern
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
